import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Button } from './primitives';
import { api, ApiError } from '../lib/api';
import { qk } from '../api/hooks';
import { EmailRecipientsInput, useEmailSuggestions } from './EmailRecipientsInput';
import { safeFileName } from '../lib/safeFileName';
import {
  COMMON_EMAIL_DOMAINS,
  findEmailTypos,
  replaceEmailInField,
} from '../lib/emailTypo';
import styles from './SendEmailModal.module.css';
import { cx } from '../lib/cx';

/**
 * Odeslání dokumentu e-mailem. Port chování z frontend/index.html:11595
 * (emailDoc → emSendPdf). Backend `POST /api/email/send` si sám vygeneruje PDF
 * z předaného HTML přes Playwright.
 *
 * `to`, `cc`, `bcc` přijímají čárkou oddělené adresy (víceadresáti).
 */

export interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Vrátí kompletní HTML dokumentu (inline CSS), které backend pošle do Playwrightu.
   * Typicky „outerHTML preview kontejneru + všechny <style>" přes helper `buildDocHtml`.
   */
  buildHtml?: () => string | Promise<string>;
  /**
   * Alternativa k `buildHtml`: hotová příloha (např. podepsané PDF). Když je
   * zadaná, backend nic negeneruje a pošle přesně tyto bajty.
   */
  buildAttachment?: () => Promise<{ base64: string; filename: string }>;
  /**
   * Další přílohy (např. soupis, ZP a předávací protokol k faktuře). Volá se
   * až při odeslání; chyba přípravy zastaví odeslání a ukáže se v dialogu.
   */
  extraAttachments?: () => Promise<Array<{ base64: string; filename: string; mime_type?: string }>>;
  /** Text pod adresami — co se přikládá navíc. */
  attachmentsNote?: string;
  /** Typ dokumentu (např. 'predavaci', 'faktura'). Posílá se na backend. */
  docType: string;
  /** Titulek v dialogu + default subject. */
  docTitle: string;
  /** Volitelné — přednastavené hodnoty. */
  defaultTo?: string;
  defaultCc?: string;
  defaultSubject?: string;
  defaultBody?: string;
  /** Řádek „meta" nad formulářem: stavba, zakázka, firma — čistě info. */
  meta?: string;
}

/** Formát adresy: něco@domena.tld, bez mezer a diakritiky, koncovka z písmen. */
const EMAIL_FORMAT = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;

/** Srozumitelná hláška — backend teď vrací důvod (SMTP odmítl příjemce apod.). */
function emailErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.detail as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (e.status === 401) return 'Vypršelo přihlášení — přihlaste se prosím znovu.';
    return `Nepodařilo se odeslat (chyba ${e.status}).`;
  }
  return e instanceof Error && e.message ? e.message : 'Nepodařilo se odeslat.';
}

export function SendEmailModal({
  open,
  onClose,
  buildHtml,
  buildAttachment,
  extraAttachments,
  attachmentsNote,
  docType,
  docTitle,
  defaultTo = '',
  defaultCc = '',
  defaultSubject,
  defaultBody,
  meta,
}: SendEmailModalProps) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(Boolean(defaultCc));
  const [subject, setSubject] = useState(defaultSubject ?? docTitle);
  const [body, setBody] = useState(
    defaultBody ??
      'Dobrý den,\n\nv příloze zasíláme dokument: ' + docTitle + '.\n\nS pozdravem',
  );
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'progress'; msg: string }
    | { kind: 'ok'; msg: string }
    | { kind: 'err'; msg: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (!open) return;
    setTo(defaultTo);
    setCc(defaultCc);
    setBcc('');
    setShowCc(Boolean(defaultCc));
    setSubject(defaultSubject ?? docTitle);
    setBody(
      defaultBody ??
        'Dobrý den,\n\nv příloze zasíláme dokument: ' + docTitle + '.\n\nS pozdravem',
    );
    setStatus({ kind: 'idle' });
  }, [open, defaultTo, defaultCc, defaultSubject, defaultBody, docTitle]);

  const sending = status.kind === 'progress';
  const qc = useQueryClient();
  // Historie adres + adresář firem pro našeptávač (načítá se jen při otevřeném dialogu).
  const suggestions = useEmailSuggestions(open);

  // Nápověda na překlep v doméně (gmial.com → gmail.com). Známé domény = běžní
  // poskytovatelé + adresář firem + firemní seznam + adresy použité aspoň 2×
  // (jednou použitá může být sama překlep, uložený před zavedením kontroly).
  const knownDomains = useMemo(() => {
    const s = new Set(COMMON_EMAIL_DOMAINS);
    for (const sg of suggestions) {
      if (sg.source === 'history' && sg.useCount < 2) continue;
      const d = sg.email.split('@')[1];
      if (d) s.add(d);
    }
    return s;
  }, [suggestions]);
  // Adresy, které uživatel potvrdil jako správné („Je správně").
  const [confirmedOk, setConfirmedOk] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setConfirmedOk(new Set());
  }, [open]);
  const computeTypoHints = (vals: { to: string; cc: string; bcc: string }) =>
    (['to', 'cc', 'bcc'] as const).flatMap((field) =>
      findEmailTypos(vals[field], knownDomains)
        .filter((h) => !confirmedOk.has(h.address.toLowerCase()))
        .map((h) => ({ ...h, field })),
    );
  // Během psaní („gmail.co" → ještě nedopsané „m") nápověda neblikne —
  // zobrazí se až 700 ms po posledním úhozu. Při odeslání se kontroluje hned.
  const [typedVals, setTypedVals] = useState({ to, cc, bcc });
  // Po úpravě adres stará chybová hláška zmizí (už se netýká aktuálních adres).
  useEffect(() => {
    setStatus((s) => (s.kind === 'err' ? { kind: 'idle' } : s));
  }, [to, cc, bcc]);
  useEffect(() => {
    const t = window.setTimeout(() => setTypedVals({ to, cc, bcc }), 700);
    return () => window.clearTimeout(t);
  }, [to, cc, bcc]);
  const typoHints = useMemo(() => computeTypoHints(typedVals), [typedVals, knownDomains, confirmedOk]);

  function applyTypoFix(field: 'to' | 'cc' | 'bcc', from: string, fixed: string) {
    const next = { to, cc, bcc, [field]: replaceEmailInField({ to, cc, bcc }[field], from, fixed) };
    setTo(next.to);
    setCc(next.cc);
    setBcc(next.bcc);
    setTypedVals(next); // upozornění zmizí hned, ne až po prodlevě pro psaní
    setStatus({ kind: 'idle' });
  }

  async function handleSend() {
    const splitList = (v: string) =>
      v
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
    const toList = splitList(to);
    // Našeptávač nechává za adresou „, " → do hlavičky Cc poslat čistý seznam.
    const ccClean = splitList(cc).join(', ') || null;
    const bccClean = splitList(bcc).join(', ') || null;
    if (toList.length === 0) {
      setStatus({ kind: 'err', msg: 'Zadejte alespoň jednoho příjemce.' });
      return;
    }
    // Rychlá kontrola formátu hned v prohlížeči (doménu přes DNS ověří backend).
    const bad = [...toList, ...splitList(cc), ...splitList(bcc)].filter(
      (a) => !EMAIL_FORMAT.test((a.match(/<([^>]*)>/)?.[1] ?? a).trim()),
    );
    if (bad.length > 0) {
      setStatus({
        kind: 'err',
        msg: `✗ Neplatná e-mailová adresa: ${bad.join(', ')} — zkontrolujte překlep.`,
      });
      return;
    }
    if (computeTypoHints({ to, cc, bcc }).length > 0) {
      setTypedVals({ to, cc, bcc }); // ukázat upozornění hned, bez čekání
      setStatus({
        kind: 'err',
        msg: '✗ Adresa vypadá jako překlep — opravte ji, nebo potvrďte „Je správně" v upozornění pod adresami.',
      });
      return;
    }
    setStatus({ kind: 'progress', msg: '⏳ Generuji PDF a odesílám…' });
    try {
      const attachments = extraAttachments ? await extraAttachments() : undefined;
      if (buildAttachment) {
        const att = await buildAttachment();
        await api.post<{ ok: boolean }>('/email/send', {
          to: toList.join(', '),
          subject,
          body,
          cc: ccClean,
          bcc: bccClean,
          attachment_base64: att.base64,
          attachment_filename: att.filename,
          attachments,
        });
        setStatus({ kind: 'ok', msg: `✓ Odesláno příjemcům (${toList.length}).` });
        void qc.invalidateQueries({ queryKey: qk.emailRecipients });
        window.setTimeout(() => onClose(), 1600);
        return;
      }
      if (!buildHtml) throw new Error('Chybí obsah dokumentu k odeslání.');
      const html = await buildHtml();
      await api.post<{ ok: boolean }>('/email/send', {
        to: toList.join(', '),
        subject,
        body,
        cc: ccClean,
        bcc: bccClean,
        doc_type: docType,
        // `print_media`: příloha se vyrenderuje ve stejném (tiskovém) režimu
        // jako PDF stažené tlačítkem „PDF" — bez tmavé hlavičky obrazovky a
        // ovládacích prvků. `filename`: příjemce dostane „Faktura_2026-001.pdf"
        // místo obecného „faktura.pdf".
        doc_data: { html, print_media: true, filename: safeFileName(docTitle) },
        attachment_format: 'pdf',
        attachments,
      });
      setStatus({
        kind: 'ok',
        msg: `✓ Odesláno příjemcům (${toList.length}).`,
      });
      void qc.invalidateQueries({ queryKey: qk.emailRecipients });
      window.setTimeout(() => {
        onClose();
      }, 1600);
    } catch (e) {
      setStatus({ kind: 'err', msg: `✗ ${emailErrorMessage(e)}` });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Odeslat e-mailem — ${docTitle}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Zavřít
          </Button>
          <Button variant="primary" onClick={handleSend} disabled={sending}>
            {sending ? 'Odesílám…' : '📧 Odeslat'}
          </Button>
        </>
      }
    >
      <div className={styles.root}>
        {meta && <div className={styles.headMeta}>{meta}</div>}
        {attachmentsNote && <div className={styles.headMeta}>📎 {attachmentsNote}</div>}

        {status.kind !== 'idle' && (
          <div
            className={cx(
              styles.status,
              status.kind === 'progress' && styles.progress,
              status.kind === 'ok' && styles.ok,
              status.kind === 'err' && styles.err,
            )}
          >
            {status.msg}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>Komu *</label>
          <EmailRecipientsInput
            placeholder="prijemce@firma.cz, dalsi@email.cz"
            value={to}
            onChange={setTo}
            suggestions={suggestions}
            pickerTitle="Komu"
            autoFocus
          />
          <span className={styles.hint}>
            Stačí napsat pár písmen — nabídnou se dříve použité adresy a kontakty z adresáře.
            👤 = výběr ze seznamu. Víc adresátů oddělte čárkou.
          </span>
        </div>

        {!showCc ? (
          <button
            type="button"
            className={styles.ccToggle}
            onClick={() => setShowCc(true)}
          >
            + Přidat kopie (CC/BCC)
          </button>
        ) : (
          <>
            <div className={styles.field}>
              <label className={styles.label}>Kopie (CC)</label>
              <EmailRecipientsInput
                placeholder="cc1@firma.cz, cc2@firma.cz"
                value={cc}
                onChange={setCc}
                suggestions={suggestions}
                pickerTitle="Kopie (CC)"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Skrytá kopie (BCC)</label>
              <EmailRecipientsInput
                placeholder="bcc@firma.cz"
                value={bcc}
                onChange={setBcc}
                suggestions={suggestions}
                pickerTitle="Skrytá kopie (BCC)"
              />
            </div>
          </>
        )}

        {typoHints.length > 0 && (
          <div className={styles.typoBox} role="alert">
            {typoHints.map((h) => (
              <div key={`${h.field}:${h.address}`} className={styles.typoRow}>
                <span>
                  ⚠ <strong>{h.address}</strong> — nemysleli jste{' '}
                  <strong>{h.suggestion}</strong>?
                </span>
                <span className={styles.typoActions}>
                  <button
                    type="button"
                    className={styles.typoFix}
                    onClick={() => applyTypoFix(h.field, h.address, h.suggestion)}
                  >
                    Opravit
                  </button>
                  <button
                    type="button"
                    className={styles.typoKeep}
                    onClick={() => {
                      setConfirmedOk((s) => new Set(s).add(h.address.toLowerCase()));
                      setStatus({ kind: 'idle' });
                    }}
                  >
                    Je správně
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>Předmět</label>
          <input
            className={styles.input}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Text zprávy</label>
          <textarea
            className={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
          />
          <span className={styles.hint}>
            PDF přílohu vygeneruje server z náhledu dokumentu vpravo. Nic stahovat nemusíte.
          </span>
        </div>
      </div>
    </Modal>
  );
}
